// Libraries
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

//Components
import { Loader } from 'components/lib';
import Header from './parts/Header';
import Declined from './parts/Declined';
import Review from './parts/Review';
import Warning from './parts/Warning';

//Helpers
import QuoteUtils from 'helpers/quoteUtils';
import { noFeeAgencies, Providers } from 'helpers/constants';
import { Errors, ErrorsMessages } from 'helpers/errors';

const quoteUtils = new QuoteUtils();

export function ReviewQuote() {
  const { state } = useLocation();
  const { data } = state;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [quote, setQuote] = useState({});
  const [declinedReason, setDeclinedReason] = useState(null);
  const [liveDataUnavailable, setLiveDataUnavailable] = useState(false);

  const isSubmissionInvalid =
    !loading && data?.submission_number === 'N/A' && !quote?.id;
  const isDeclined = !loading && data?.status === 'declined';
  const showErrorMessage =
    !loading && error === Errors.SOMETHING_WENT_WRONG;
  const isValid =
    !loading &&
    !isSubmissionInvalid &&
    !isDeclined &&
    !showErrorMessage;

  async function getQuoteData() {
    if (!data) {
      setLoading(false);

      return { success: false, error: 'No data provided' };
    }

    try {
      const agencyResponse = await axios.get('/api/account');
      const internalResponse = await axios.get(
        `/api/quote/${data?.id}`
      );
      let coverwhaleResponse = null;
      if (data?.submission_number && data.submission_number !== 'N/A') {
        try {
          coverwhaleResponse = await axios.get(
            `/api/quote/info/${data?.submission_number}`
          );
        } catch (lookupError) {
          console.error(lookupError);
        }
      }

      const currentAgency = agencyResponse?.data?.data;
      const coverwhaleQuote = coverwhaleResponse?.data?.data;
      const internalQuote = internalResponse?.data?.data;
      const hasLiveCoverWhaleData =
        Boolean(coverwhaleQuote?.premiumFinanceDetails) &&
        coverwhaleQuote?.premiumFinanceDetails?.downPayment !== null &&
        coverwhaleQuote?.premiumFinanceDetails?.paymentAmount !== null &&
        coverwhaleQuote?.premiumFinanceDetails?.effectiveAPR !== null;

      if (internalQuote.status === 'declined') {
        setDeclinedReason(data.error_reason);
        setLoading(false);

        return { success: true };
      }

      if (!hasLiveCoverWhaleData) {
        setLiveDataUnavailable(true);
        setQuote({
          coverages: {},
          financialDetails: null,
          quote: internalQuote.quote,
          company_name: internalQuote?.companyData?.name || '',
          submission_number: internalQuote.submission_number || data.submission_number,
          id: data.id,
          status: internalQuote.status,
          effective_date: internalQuote.effective_date || data.effective_date,
          carrier: internalQuote.carrier,
        });
        setLoading(false);

        return { success: true };
      }

      const rawCoverages = {
        ...coverwhaleQuote,
        towingLaborStorage: {
          value: Number(internalQuote?.quote?.limits?.limitTowingStorage) || 0,
          included: true,
        },
        waiverSubrogation: {
          included:
            internalQuote?.quote?.limits?.insuredWaiverSubrogation === 'Y',
        },
        UIIAintermodal: {
          included: internalQuote?.quote?.operations?.UIIAintermodal === 'Y',
        },
        primaryNoncontributoryAL: {
          included:
            internalQuote?.quote?.limits?.primaryNoncontributoryAL === 'Y',
        },
        primaryNoncontributoryTGL: {
          included:
            internalQuote?.quote?.limits?.primaryNoncontributoryTGL === 'Y',
        },
        trailerInterchange: {
          limit: coverwhaleQuote?.coverages?.apd?.trailer_interchange_limit
            || internalQuote?.quote?.limits?.trailerInterchangeLimit
            || null,
          carrier: coverwhaleQuote?.coverages?.apd?.carrier || null,
          included: internalQuote?.quote?.limits?.trailerInterchange === 'Y',
        },
        commoditiesRefrigeration: {
          included: internalQuote?.quote?.commoditiesRefrigeration === 'Y',
        },
      };

      const { coverages, financialDetails } = quoteUtils.calculate(
        rawCoverages,
        Providers.CoverWhale,
        noFeeAgencies.includes(currentAgency?.id)
      );

      await axios.patch(`/api/quote/${data?.id}`, {
        premium: Number(financialDetails.premium),
      });

      setLiveDataUnavailable(false);
      setQuote({
        coverages,
        financialDetails,
        quote: internalQuote.quote,
        company_name: internalQuote?.companyData?.name || '',
        submission_number: internalQuote.submission_number || data.submission_number,
        id: data.id,
        status: internalQuote.status,
        effective_date: internalQuote.effective_date || data.effective_date,
        carrier: internalQuote.carrier,
      });
      setLoading(false);

      return { success: true };
    } catch (error) {
      console.error(error);
      setLoading(false);

      return {
        success: false,
        error:
          error?.response?.status === 400
            ? Errors.SOMETHING_WENT_WRONG
            : Errors.DATA_NOT_READY,
      };
    }
  }

  async function runQuoteHandler() {
    const result = await getQuoteData();

    if (!result?.success) {
      setError(result.error);
    }
  }

  useEffect(() => {
    runQuoteHandler();
  }, []);

  return (
    <>
      {loading && <Loader />}
      {showErrorMessage && (
        <Warning message={ErrorsMessages.SOMETHING_WENT_WRONG} />
      )}
      {isSubmissionInvalid && (
        <Warning
          message={ErrorsMessages.QUOTE_COVERWHALE_INVALID_SUBMISSION}
        />
      )}
      {isDeclined && <Declined reason={declinedReason} />}
      {isValid && (
        <>
          {liveDataUnavailable && (
            <div
              style={{
                backgroundColor: '#FFFDE6',
                color: '#A16207',
                padding: 15,
                borderRadius: 8,
                marginBottom: 16,
              }}
            >
              Live Cover Whale quote details are temporarily unavailable. Showing the saved quote snapshot instead.
            </div>
          )}
          <Header data={quote} />
          <Review data={quote} />
        </>
      )}
    </>
  );
}
